import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { StyledTheme } from '../../styles/styleTheme';
import { CustomModal } from '../common/CustomModal/CustomModal';

interface BulkActionsToolbarProps {
  selectedCount: number;
  resyncableCount: number;
  busy: boolean;
  canDelete: boolean;
  onClearSelection: () => void;
  onConfirmBulkResync: () => void;
  onConfirmBulkDelete: () => void;
}

export const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedCount,
  resyncableCount,
  busy,
  canDelete,
  onClearSelection,
  onConfirmBulkResync,
  onConfirmBulkDelete,
}) => {
  if (selectedCount === 0) {
    return null;
  }

  const skippedCount = Math.max(0, selectedCount - resyncableCount);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        mb: 2,
        backgroundColor: '#f5f7f8',
        border: `1px solid ${StyledTheme.grayLight}`,
        borderRadius: 1,
      }}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <Typography variant='body2' fontWeight={600}>
          {selectedCount} selected
        </Typography>
        <Button size='small' variant='text' onClick={onClearSelection} disabled={busy}>
          Clear
        </Button>
      </Stack>
      <Stack direction='row' spacing={1}>
        <CustomModal
          showChildrenDefault={false}
          modalProps={{
            style: { width: '480px', maxWidth: '90vw', height: 'auto', padding: '24px' },
          }}
          buttonProps={{
            buttonText: busy ? 'Working…' : 'Resync selected',
            disabled: busy || resyncableCount === 0,
            startIcon: busy ? (
              <CircularProgress style={{ color: '#ffffff' }} size={16} />
            ) : (
              <RefreshIcon />
            ),
            sx: {
              '&.custom-modal-button': {
                backgroundColor: StyledTheme.tealDark,
                color: StyledTheme.white,
                textTransform: 'capitalize',
                '&:hover': { backgroundColor: StyledTheme.tealDark },
              },
            },
          }}
          actionButtons={{
            cancelButtonProps: { buttonText: 'Cancel' },
            continueButtonProps: {
              buttonText: 'Confirm resync',
              sx: { backgroundColor: StyledTheme.tealDark, color: StyledTheme.white },
            },
          }}
          onContinue={onConfirmBulkResync}>
          <Box p={2}>
            <Typography variant='h6' gutterBottom>
              Resync {resyncableCount} claim{resyncableCount === 1 ? '' : 's'}?
            </Typography>
            <Typography variant='body2'>
              Each claim will be resynced individually. Failures will be reported per-claim once
              the run completes.
            </Typography>
            {skippedCount > 0 && (
              <Typography variant='body2' sx={{ mt: 1, color: 'text.secondary' }}>
                {skippedCount} processed claim{skippedCount === 1 ? '' : 's'} in your selection
                will be skipped.
              </Typography>
            )}
          </Box>
        </CustomModal>

        {canDelete && (
          <CustomModal
            showChildrenDefault={false}
            modalProps={{
              style: { width: '480px', maxWidth: '90vw', height: 'auto', padding: '24px' },
            }}
            buttonProps={{
              buttonText: busy ? 'Working…' : 'Delete selected',
              disabled: busy,
              startIcon: busy ? (
                <CircularProgress style={{ color: '#ffffff' }} size={16} />
              ) : (
                <DeleteIcon />
              ),
              sx: {
                '&.custom-modal-button': {
                  backgroundColor: '#d32f2f',
                  color: StyledTheme.white,
                  textTransform: 'capitalize',
                  '&:hover': { backgroundColor: '#b71c1c' },
                },
              },
            }}
            actionButtons={{
              cancelButtonProps: { buttonText: 'Cancel' },
              continueButtonProps: {
                buttonText: 'Confirm delete',
                sx: { backgroundColor: '#d32f2f', color: StyledTheme.white },
              },
            }}
            onContinue={onConfirmBulkDelete}>
            <Box p={2}>
              <Typography variant='h6' gutterBottom>
                Delete {selectedCount} claim{selectedCount === 1 ? '' : 's'}?
              </Typography>
              <Typography variant='body2'>
                The selected claims will be hidden from the calendar and skipped on future syncs.
                This cannot be undone from the dashboard.
              </Typography>
            </Box>
          </CustomModal>
        )}
      </Stack>
    </Box>
  );
};
